import { headerFormat, clearExcelData } from 'utils/excelUtils/ExcelDataPopulate';
import { getTSHeaders } from './columnGenerationUtil';
import { transformedTSRowsToExcelFormat, transformedTPRowsToExcelFormat } from './rowGenerationUtil';

function buildHeaderMatrix(headers) {
    const matrix = [];
    const merges = [];
    const maxDepth = getMaxDepth(headers);

    function fillMatrix(nodes, row = 0, colStart = 0) {
        let col = colStart;
        for (const node of nodes) {
            const childCount = getLeafCount(node);
            while (matrix.length <= row) matrix.push([]);
            matrix[row][col] = node.label;
            if (node.children && node.children.length > 0) {
                merges.push({ startRow: row, startCol: col, rowSpan: 1, colSpan: childCount });
                fillMatrix(node.children, row + 1, col);
            } else {
                merges.push({ startRow: row, startCol: col, rowSpan: maxDepth - row, colSpan: 1 });
            }
            col += childCount;
        }
        return col;
    }

    fillMatrix(headers);

    const maxCols = Math.max(...matrix.map(r => r.length));
    for (const row of matrix) {
        while (row.length < maxCols) {
            row.push('');
        }
    }
    return { matrix, merges };
}

function getMaxDepth(nodes) {
    return Math.max(
        ...nodes.map(n =>
            n.children ? 1 + getMaxDepth(n.children) : 1
        )
    );
}

function getLeafCount(node) {
    if (!node.children) return 1;
    return node.children.reduce((sum, c) => sum + getLeafCount(c), 0);
}

export const mergeColumnsAndRender = async (response, inputData, columns, mappings, accountsMap, callback) => {
    // const headerStructure = [
    //     {
    //         label: "325LLC - 325 Fillmore LLC", children: [{
    //                                                             label: "End Date"
    //                                                         },{
    //                                                             label: "Cash & Short Term Der.",
    //                                                             children: [
    //                                                                 { label: "Cash & Equivalents", children: [{ label: "Misc", children: [{ label: "Account" }] }] }
    //                                                             ],
    //                                                         },
    //                                                         {
    //                                                             label : "Fixed Income",
    //                                                             children : [{ label: "Asset Backed Bonds", children: [{ label: "Misc", children: [{ label: "Account" }] }] },
    //                                                                      { label: "Collateral Mortage", children: [{ label: "Misc", children: [{ label: "Account" }] }] },
    //                                                                     { label: "Other Fixed Income", children: [{ label: "Misc", children: [{ label: "Account" }] }] }]
    //                                                         },
    //                                                         {
    //                                                             label : "Pending Cash",
    //                                                             children : [{ label: "Misc", children: [{ label: "Account"}] }]
    //                                                         }

    //         ]
    //     }];

    // Remove data from previous results
    if (inputData.outputEndRange) {
        const outputSheetname = inputData.outputEndRange.sheetName;
        clearExcelData(inputData.outputEndRange.cellRange, outputSheetname);
    }

    await Excel.run(async (context) => {
        const { matrix, merges } = buildHeaderMatrix(columns);
        const rows = transformedTSRowsToExcelFormat(response ?.rows, inputData, mappings, accountsMap);
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        const inputRowCellParts =
            inputData.cellNumber &&
            inputData.cellNumber.match(/^([A-Za-z]{1,3})([0-9]{1,6})$/);
        const anchorCell = sheet.getRange(inputRowCellParts[0]);
        anchorCell.load("rowIndex");
        anchorCell.load('columnIndex');
        await context.sync();

        // Header rows and columns
        const startRow = anchorCell.rowIndex + 5;
        const startCol = anchorCell.columnIndex;
        const rowCount = matrix.length;
        const colCount = matrix[0].length;

        const startRange = sheet.getRange(
            `${inputRowCellParts[1]}${startRow}`,
        );
        const endRange = startRange.getResizedRange(0, colCount - 1);
        endRange.load('address');
        await context.sync();

        // Output result data range
        const outputEndColumnParts = endRange.address
            .split(':')[1]
            .match(/^([A-Za-z]{1,3})([0-9]{1,6})$/);
        const outPutEndCellNum =
            parseInt(outputEndColumnParts[2], 10) + rows.length + rowCount;
        const outputEndRange = `${outputEndColumnParts[1]}${outPutEndCellNum}`;

        // Range for data to be populated
        const range = sheet.getRangeByIndexes(startRow, startCol, rowCount, colCount);
        range.values = matrix;
        headerFormat(range);
        range.format.autofitColumns();
        for (const m of merges) {
            if (m.rowSpan > 1 || m.colSpan > 1) {
                const mergeRange = sheet.getRangeByIndexes(startRow + m.startRow, startCol + m.startCol, m.rowSpan, m.colSpan);
                mergeRange.merge();
            }
        }

        const startRowForData = startRow + matrix.length;
        const rowRange = sheet.getRangeByIndexes(startRowForData, startCol, rows.length, rows[0].length);
        rowRange.values = rows;
        rowRange.format.autofitColumns();

        // update output range for data
        callback(inputData.sheetName, `${inputRowCellParts[1]}${startRow}:${outputEndRange}`);

        await context.sync();
    });
}

const splitCellValue = splitValue => {
	const parts = splitValue && splitValue.match(/^([A-Za-z]{1,3})([0-9]{1,6})$/);
	const cellColumn = parts && parts[1];
	const cellRow = parts && parseInt(parts[2], 10);
	return { cellColumn, cellRow };
};

function getColumnIndexFromId(columnId) {
    let index = 0;
    for (let i = 0; i < columnId.length; i++) {
        index *= 26;
        index += columnId.charCodeAt(i) - 'A'.charCodeAt(0) + 1;
    }
    return index - 1; // zero-based index
}

export const renderDatasetTSRunResponse = (response, inputData, columns, mappings, accountsMap, callback) => {
    // Remove data from previous results
    if (inputData.outputEndRange) {
        const outputSheetname = inputData.outputEndRange.sheetName;
        clearExcelData(inputData.outputEndRange.cellRange, outputSheetname);
    }

    const { matrix, merges } = buildHeaderMatrix(columns);
    const rows = transformedTSRowsToExcelFormat(response ?.rows, inputData, mappings, accountsMap);

    Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(inputData.outputEndRange.sheetName);
        const outputCellNo = inputData.outputEndRange.cellRange.split(':')[0];
        // const endOutputCellValue = inputData.outputEndRange.cellRange.split(':')[1];
        const outputStartRange = splitCellValue(outputCellNo);
        const startRow = outputStartRange.cellRow;
        const startCol = getColumnIndexFromId(outputStartRange.cellColumn);

        // Range for data to be populated
        const range = sheet.getRangeByIndexes(startRow, startCol, matrix.length, matrix[0].length);
        range.values = matrix;
        headerFormat(range);
        range.format.autofitColumns();
        for (const m of merges) {
            if (m.rowSpan > 1 || m.colSpan > 1) {
                const mergeRange = sheet.getRangeByIndexes(startRow + m.startRow, startCol + m.startCol, m.rowSpan, m.colSpan);
                mergeRange.merge();
            }
        }

        const startRowForData = startRow + matrix.length;
        const rowRange = sheet.getRangeByIndexes(startRowForData, startCol, rows.length, rows[0].length);
        rowRange.values = rows;
        rowRange.format.autofitColumns();

        // update output range for data
        callback(inputData.sheetName, inputData.outputEndRange.cellRange);

        await context.sync();
    });
}

export const mergeTPColumnsAndRender = async (response, inputData, columns, mappings, headers, accountsMap,callback) => {
    // let columns = [
	// 			    {
	// 					label: "Account Breakdown"
	// 				}, {
	// 					label: "Account",
	// 					children: [
	// 						{ label: "Market Value", children: null },
	// 						{ label: "Rate of Return", children: [{ label: "Daily" }, { label: "MTD" }] }

	// 					],
	// 				},
	// 				{
	// 					label: "Benchmark (FTSE Russell 1000 Value)",
	// 					children: [{ label: "Rate of Return", children: [{ label: "Daily" }, { label: "MTD" }] }
	// 					]
	// 				},
	// 			];

    // Remove data from previous results
    if (inputData.outputEndRange) {
        const outputSheetname = inputData.outputEndRange.sheetName;
        clearExcelData(inputData.outputEndRange.cellRange, outputSheetname);
    }

    await Excel.run(async (context) => {
        const { matrix, merges } = buildHeaderMatrix(columns);
        const rows = transformedTPRowsToExcelFormat(inputData, accountsMap, response, headers, mappings);
	
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        const inputRowCellParts =
            inputData.cellNumber &&
            inputData.cellNumber.match(/^([A-Za-z]{1,3})([0-9]{1,6})$/);
        const anchorCell = sheet.getRange(inputRowCellParts[0]);
        anchorCell.load("rowIndex");
        anchorCell.load('columnIndex');
        await context.sync();

        // Header rows and columns
        const startRow = anchorCell.rowIndex + 5;
        const startCol = anchorCell.columnIndex;
        const rowCount = matrix.length;
        const colCount = matrix[0].length;

        const startRange = sheet.getRange(
            `${inputRowCellParts[1]}${startRow}`,
        );
        const endRange = startRange.getResizedRange(0, colCount - 1);
        endRange.load('address');
        await context.sync();

        // Output result data range
        const outputEndColumnParts = endRange.address
            .split(':')[1]
            .match(/^([A-Za-z]{1,3})([0-9]{1,6})$/);
        const outPutEndCellNum =
            parseInt(outputEndColumnParts[2], 10) + rows.length + rowCount;
        const outputEndRange = `${outputEndColumnParts[1]}${outPutEndCellNum}`;

        // Range for data to be populated
        const range = sheet.getRangeByIndexes(startRow, startCol, rowCount, colCount);
        range.values = matrix;
        headerFormat(range);
        range.format.autofitColumns();
        for (const m of merges) {
            if (m.rowSpan > 1 || m.colSpan > 1) {
                const mergeRange = sheet.getRangeByIndexes(startRow + m.startRow, startCol + m.startCol, m.rowSpan, m.colSpan);
                mergeRange.merge();
            }
        }

        const startRowForData = startRow + matrix.length;
        const rowRange = sheet.getRangeByIndexes(startRowForData, startCol, rows.length, rows[0]?.length ?? 0);
        rowRange.values = rows;
        rowRange.format.autofitColumns();

        // update output range for data
        callback(inputData.sheetName, `${inputRowCellParts[1]}${startRow}:${outputEndRange}`);

        await context.sync();
    });
}

export const renderDatasetTPRunResponse = async (response, inputData, columns, mappings, headers, accountsMap,callback) => {
    // Remove data from previous results
    if (inputData.outputEndRange) {
        const outputSheetname = inputData.outputEndRange.sheetName;
        clearExcelData(inputData.outputEndRange.cellRange, outputSheetname);
    }

    const { matrix, merges } = buildHeaderMatrix(columns);
    const rows = transformedTPRowsToExcelFormat(inputData, accountsMap, response, headers, mappings);
	
    await Excel.run(async (context) => {        
        const sheet = context.workbook.worksheets.getItem(inputData.outputEndRange.sheetName);

        const outputCellNo = inputData.outputEndRange.cellRange.split(':')[0];
        // const endOutputCellValue = inputData.outputEndRange.cellRange.split(':')[1];
        const outputStartRange = splitCellValue(outputCellNo);
        const startRow = outputStartRange.cellRow;
        const startCol = getColumnIndexFromId(outputStartRange.cellColumn);

        // Range for data to be populated
        const range = sheet.getRangeByIndexes(startRow, startCol, matrix.length, matrix[0].length);
        range.values = matrix;
        headerFormat(range);
        range.format.autofitColumns();
        for (const m of merges) {
            if (m.rowSpan > 1 || m.colSpan > 1) {
                const mergeRange = sheet.getRangeByIndexes(startRow + m.startRow, startCol + m.startCol, m.rowSpan, m.colSpan);
                mergeRange.merge();
            }
        }

        const startRowForData = startRow + matrix.length;
        const rowRange = sheet.getRangeByIndexes(startRowForData, startCol, rows.length, rows[0].length);
        rowRange.values = rows;
        rowRange.format.autofitColumns();

        // update output range for data
        callback(inputData.sheetName, inputData.outputEndRange.cellRange);

        await context.sync();
    });
}